import React, { useCallback, useEffect, useState } from 'react';
import { Box, FlexControlledForm, useDebounceState } from '@common-fe/common-fe';

import { WarnModal } from '@/components/elements';
import { ExpandedOption } from '@/modules/HealthPlan/queries/usePlansByPlanYear.query.ts';

import { useSubmitFormStore } from '../../stores';
import { useHealthPlanNameFields } from '../HealthPlanName/hooks';
import {
  HealthPlanNamePayload,
  useHealthPlanNameStore,
} from '../HealthPlanName/stores/useHealthPlanName.store';
interface Props {
  isEditMode?: boolean;
  isTemplateMode?: boolean;
  isHealthPlanActive?: boolean;
  arePlansEditable?: boolean;
  planYearFormatedOptions?: ExpandedOption[];
  onDirty?: (isDirty: boolean) => void;
}
const Additional: React.FC<Props> = ({
  isEditMode,
  isTemplateMode,
  isHealthPlanActive,
  arePlansEditable,
  planYearFormatedOptions,
  onDirty,
}) => {
  const [isConfirmationModalVisible, setIsConfirmationModalVisible] = useState(false);
  const [removedInitialTypes, setRemovedInitialTypes] = useState<string[]>([]);

  const saveValidate = useSubmitFormStore((state) => state.saveValidate);
  const activateValidate = useSubmitFormStore((state) => state.activateValidate);
  const { stateFields: fields, createCarrierModals } = useHealthPlanNameFields({
    isActivationMode: activateValidate,
    isTemplateMode,
    isEditMode,
    arePlansEditable,
    isAdditionalSection: true,
    planYearFormatedOptions,
  });

  const sourceState = useHealthPlanNameStore((state) => state.sourceState);
  const handleSetState = useHealthPlanNameStore((state) => state.setState);
  const showErrors = useHealthPlanNameStore((state) => state.showErrors);
  const { setValues, values } = useDebounceState<Partial<HealthPlanNamePayload>>();

  const handleSubmit = useCallback(() => {
    setIsConfirmationModalVisible(false);
  }, []);

  useEffect(() => {
    if (!values) return;
    const sanitizedPlans = values.plans?.filter((plan): plan is string => typeof plan === 'string' && !!plan);
    handleSetState({ ...values, plans: sanitizedPlans });
    const initialTypes = sourceState?.healthPlanTypes || [];
    const nextTypes = values?.healthPlanTypes || [];

    setRemovedInitialTypes((prev) => {
      const filtered = prev.filter((type) => !nextTypes.includes(type));
      if (filtered.length !== prev.length) return filtered;
      return prev;
    });

    const removed = initialTypes.filter((type) => !nextTypes.includes(type));
    const newlyRemoved = removed.filter((type) => !removedInitialTypes.includes(type));

    if (newlyRemoved?.length > 0 && isHealthPlanActive && !isTemplateMode) {
      setIsConfirmationModalVisible(true);
      setRemovedInitialTypes((prev) => [...prev, ...newlyRemoved]);
    }
  }, [
    handleSetState,
    isHealthPlanActive,
    isTemplateMode,
    values,
    sourceState?.healthPlanTypes,
    removedInitialTypes,
  ]);

  return (
    <Box background="canvas" round="container1Round">
      <FlexControlledForm
        testId="health_plan_additional"
        fields={fields}
        showError={showErrors || saveValidate || activateValidate}
        editMode={isEditMode}
        onChangeValues={setValues}
        onDirty={onDirty}
        // isDisabledMode
        isThinMode
      />
      {createCarrierModals}
      <WarnModal
        testId="confirmation_modal"
        visible={isConfirmationModalVisible}
        onSetVisible={setIsConfirmationModalVisible}
        header={`You are updating the types of services allowed for ${sourceState?.healthPlanName || ''}`}
        helptext="Previously configured payment rules may be removed. Please review payment rules and configurations to verify prior to saving."
        submitButtonText="Got It!"
        isPendingIcon
        onSubmit={handleSubmit}
      />
    </Box>
  );
};
export default Additional;
